import { useNavigate } from '@tanstack/react-router'
import { Mic, MicOff, Video, VideoOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useMeetingSession } from '@/lib/meetings/meeting-session-context'
import { shouldShowOngoingCard } from '@/lib/meetings/meeting-session'
import { FloatingCard } from './floating-card'

/**
 * The "ongoing meeting" card (board fqw7): while the meeting session is
 * live and the user is somewhere else in the project — a chat, a document —
 * this small draggable card (the same black surface as the participants
 * dock, position remembered for the session) keeps the meeting one click
 * away: its title, a live indicator, mute/camera toggles and "Return to
 * meeting", which goes back to the route on the **current** connection.
 *
 * Rendered by the project layout inside `<main>` (its positioned ancestor),
 * so it can never be dragged under the sidebar.
 */
export function OngoingMeetingCard() {
  const { t } = useTranslation('meet')
  const navigate = useNavigate()
  const { state, media } = useMeetingSession()
  if (!shouldShowOngoingCard(state) || !state.session) return null
  const { projectId, code, meeting } = state.session
  const reconnecting = state.status === 'reconnecting'
  const hasMedia = state.session.rtk !== null

  return (
    <FloatingCard
      storageKey="hub:meet:ongoing"
      anchor={{ right: 16, bottom: 16 }}
      handleLabel={t('ongoing.title')}
      className="w-64 max-w-[calc(100%-1rem)]"
      handleExtra={
        <span
          data-testid="ongoing-live"
          className="flex items-center gap-1 text-[10px] font-semibold tracking-wide text-zinc-300 uppercase"
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              reconnecting ? 'bg-amber-400' : 'animate-pulse bg-red-500'
            )}
            aria-hidden
          />
          {reconnecting ? t('ongoing.reconnecting') : t('ongoing.live')}
        </span>
      }
    >
      <div className="flex flex-col gap-2.5 px-3 pt-1 pb-3" data-testid="ongoing-meeting-card">
        <p className="truncate text-sm font-medium" title={meeting.title}>
          {meeting.title}
        </p>
        <div className="flex items-center gap-1.5">
          {hasMedia && (
            <>
              <CardButton
                active={media.micOn}
                label={media.micOn ? t('controls.micOn') : t('controls.micOff')}
                testId="ongoing-mic"
                onClick={() => void media.toggleMic()}
              >
                {media.micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
              </CardButton>
              <CardButton
                active={media.cameraOn}
                label={media.cameraOn ? t('controls.cameraOn') : t('controls.cameraOff')}
                testId="ongoing-camera"
                onClick={() => void media.toggleCamera()}
              >
                {media.cameraOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
              </CardButton>
            </>
          )}
          <button
            type="button"
            data-testid="ongoing-return"
            onClick={() => navigate({ to: '/$projectId/meet/$code', params: { projectId, code } })}
            className="ml-auto h-9 rounded-full bg-emerald-600 px-3.5 text-sm font-medium text-white transition-colors hover:bg-emerald-500"
          >
            {t('ongoing.return')}
          </button>
        </div>
      </div>
    </FloatingCard>
  )
}

function CardButton({
  active,
  label,
  testId,
  onClick,
  children,
}: {
  active: boolean
  label: string
  testId: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-full transition-colors',
        // A device is neutral while it works and red when it is off (board m5gc #19).
        active ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-red-600/90 hover:bg-red-600'
      )}
    >
      {children}
    </button>
  )
}
